import React, { useEffect, useMemo, useState } from "react";
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import { useSelector } from "react-redux";
import api, { apiError } from "../../lib/api";
import RenderToast from "../RenderToast";
import PaginationComp from "../PaginationComp";
import TableComp from "../TableComp";
import ConfirmModal from "../../Modal/ConfirmModal";
import useDebounce from "../../hooks/useDebounce";
import { hasPermission, selectUser } from "../../store/authSlice";
import styles from "./EntityCrudPage.module.css";

const buildFormState = (fields) =>
  fields.reduce((acc, field) => {
    acc[field.name] = field.defaultValue ?? "";
    return acc;
  }, {});

const slugify = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const areFormStatesEqual = (a, b) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a[key] === b[key]);
};

export default function EntityCrudPage({
  title,
  endpoint,
  fields,
  columns,
  perPage = 10,
  selectable = false,
  viewFields = [],
  isProtectedRow = () => false,
  permissionPage = "",
  bulkDeleteAction = "delete",
}) {
  const currentUser = useSelector(selectUser);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [form, setForm] = useState(buildFormState(fields));
  const debouncedSearch = useDebounce(search, 350);

  const slugSourceField = useMemo(
    () => fields.find((field) => field.autoSlugFrom)?.autoSlugFrom,
    [fields]
  );
  const slugSourceValue = form[slugSourceField] || "";

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await api.get(endpoint, {
        params: { search: debouncedSearch, page, per_page: perPage },
      });
      const payload = response.data?.data || {};
      setItems(payload.items || []);
      setTotal(payload.total || 0);
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, page, debouncedSearch, perPage]);

  useEffect(() => {
    setSelectedIds([]);
  }, [items]);

  useEffect(() => {
    if (!modalOpen) {
      setCurrentItem(null);
      setForm((prev) => {
        const next = buildFormState(fields);
        return areFormStatesEqual(prev, next) ? prev : next;
      });
      return;
    }

    if (currentItem) {
      const next = fields.reduce((acc, field) => {
        acc[field.name] = currentItem[field.name] ?? field.defaultValue ?? "";
        return acc;
      }, {});
      setForm((prev) => (areFormStatesEqual(prev, next) ? prev : next));
      return;
    }

    setForm((prev) => {
      const next = buildFormState(fields);
      return areFormStatesEqual(prev, next) ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, currentItem?.id, fields]);

  useEffect(() => {
    if (!slugSourceField) return;
    const slugField = fields.find((field) => field.name === "slug");
    if (!slugField) return;

    setForm((prev) => {
      const sourceValue = prev[slugSourceField] || "";
      const nextSlug = slugify(sourceValue);
      if (prev.slug === nextSlug) {
        return prev;
      }
      return {
        ...prev,
        slug: nextSlug,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugSourceValue, slugSourceField]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missingField = fields.find(
      (field) => field.required && !String(form[field.name] || "").trim()
    );
    if (missingField) {
      RenderToast({
        type: "error",
        message: `${missingField.label} is required.`,
      });
      return;
    }

    const action = currentItem ? "update" : "add";
    try {
      setSaving(true);
      const response = await api.post(endpoint, {
        action,
        ...(currentItem?.id ? { id: currentItem.id } : {}),
        ...form,
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || "Unable to save record.");
      }
      RenderToast({
        type: "success",
        message: currentItem ? `${title} updated successfully.` : `${title} created successfully.`,
      });
      setModalOpen(false);
      fetchItems();
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const response = await api.post(endpoint, {
        action: "delete",
        id: deleteTarget.id,
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || "Unable to delete record.");
      }
      RenderToast({ type: "success", message: `${title} deleted successfully.` });
      setDeleteTarget(null);
      fetchItems();
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setDeleteLoading(true);
    try {
      const response = await api.post(endpoint, {
        action: "bulk_delete",
        ids: selectedIds,
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || "Unable to delete records.");
      }
      RenderToast({ type: "success", message: `${selectedIds.length} ${title.toLowerCase()} deleted successfully.` });
      setSelectedIds([]);
      fetchItems();
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setDeleteLoading(false);
    }
  };

  const openCreate = () => {
    if (!canAdd) return;
    setCurrentItem(null);
    setForm(buildFormState(fields));
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setCurrentItem(item);
    setModalOpen(true);
  };

  const openView = (item) => setViewItem(item);

  const modalTitle = currentItem ? `Edit ${title}` : `Add ${title}`;
  const allSelected = selectable && items.length > 0 && selectedIds.length === items.filter((row) => !isProtectedRow(row)).length && items.every((row) => isProtectedRow(row) || selectedIds.includes(row.id));
  const activeViewFields = viewFields.length ? viewFields : fields.filter((field) => !field.hidden);
  const visibleFields = fields.filter((field) => !field.hidden);
  const canView = permissionPage ? hasPermission(currentUser, permissionPage, "view") : true;
  const canAdd = permissionPage ? hasPermission(currentUser, permissionPage, "add") : true;
  const canEdit = permissionPage ? hasPermission(currentUser, permissionPage, "edit") : true;
  const canDelete = permissionPage ? hasPermission(currentUser, permissionPage, "delete") : true;
  const canBulkDelete = permissionPage ? hasPermission(currentUser, permissionPage, bulkDeleteAction) : true;

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <h1>{title}</h1>
          <p>Manage {title.toLowerCase()} records.</p>
        </div>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <FiSearch />
          <input
            type="search"
            placeholder={`Search ${title.toLowerCase()}...`}
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
          />
        </div>
        <div className={styles.toolbarActions}>
          {selectable && canBulkDelete && selectedIds.length > 0 && (
            <button
              type="button"
              className={styles.deleteBtn}
              onClick={handleBulkDelete}
              disabled={deleteLoading}
            >
              <FiTrash2 />
              {deleteLoading ? "Deleting..." : "Delete Selected"}
            </button>
          )}
          {canAdd ? (
            <button type="button" className={styles.addBtn} onClick={openCreate}>
              <FiPlus />
              Add {title}
            </button>
          ) : null}
        </div>
      </div>

      <TableComp
        columns={[
          ...(selectable
            ? [
                {
                  key: "select",
                  label: (
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => {
                        const selectableIds = items
                          .filter((row) => !isProtectedRow(row))
                          .map((row) => row.id);
                        setSelectedIds(allSelected ? [] : selectableIds);
                      }}
                    />
                  ),
                  render: (row) =>
                    isProtectedRow(row) ? (
                      <span className={styles.protectedDot} title="Protected" />
                    ) : (
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.id)}
                        onChange={() =>
                          setSelectedIds((prev) =>
                            prev.includes(row.id)
                              ? prev.filter((item) => item !== row.id)
                              : [...prev, row.id]
                          )
                        }
                      />
                    ),
                },
              ]
            : []),
          ...columns,
          {
            key: "action",
            label: "Action",
            render: (row) => (
              <div className={styles.actions}>
                {canView ? (
                  <button type="button" onClick={() => openView(row)} title="View">
                    <FiEye />
                  </button>
                ) : null}
                {canEdit ? (
                  <button type="button" onClick={() => openEdit(row)} title="Edit">
                    <FiEdit2 />
                  </button>
                ) : null}
                {canDelete ? (
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(row)}
                    disabled={isProtectedRow(row)}
                    title={isProtectedRow(row) ? "Protected" : "Delete"}
                  >
                    <FiTrash2 />
                  </button>
                ) : null}
              </div>
            ),
          },
        ]}
        rows={items}
        loading={loading}
        emptyMessage={`No ${title.toLowerCase()} found.`}
      />

      <div className={styles.paginationRow}>
        <PaginationComp total={total} perPage={perPage} currentPage={page} onPageChange={setPage} />
      </div>

      {modalOpen && canAdd && (
        <div className={styles.modalOverlay} onClick={() => setModalOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHead}>
              <h3>{modalTitle}</h3>
            </div>
            <form className={styles.form} onSubmit={handleSubmit}>
                {visibleFields.map((field) => (
                  <label key={field.name} className={field.fullWidth ? styles.fullWidth : ""}>
                    <span>
                      {field.label}
                      {field.required && <span className={styles.required}>*</span>}
                    </span>
                  {field.type === "textarea" ? (
                    <textarea
                      rows={field.rows || 3}
                      value={form[field.name] || ""}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, [field.name]: e.target.value }))
                      }
                      placeholder={field.placeholder}
                    />
                  ) : field.type === "select" ? (
                    <select
                      value={form[field.name] || ""}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, [field.name]: e.target.value }))
                      }
                    >
                      {field.options?.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type || "text"}
                      value={form[field.name] || ""}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, [field.name]: e.target.value }))
                      }
                      placeholder={field.placeholder}
                    />
                  )}
                </label>
              ))}

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.primaryBtn} disabled={saving}>
                  {saving ? "Saving..." : currentItem ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!deleteTarget}
        title={`Delete ${title}`}
        message={`Are you sure you want to delete ${deleteTarget?.title || deleteTarget?.name || "this item"}?`}
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {viewItem && (
        <div className={styles.modalOverlay} onClick={() => setViewItem(null)}>
          <div className={styles.viewModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHead}>
              <h3>View {title}</h3>
            </div>
            <div className={styles.viewBody}>
              {activeViewFields.map((field) => (
                <div key={field.name} className={styles.viewRow}>
                  <span>{field.label}</span>
                  <strong>{String(viewItem[field.name] ?? "-")}</strong>
                </div>
              ))}
            </div>
            <div className={styles.modalActions}>
              <button type="button" className={styles.secondaryBtn} onClick={() => setViewItem(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
